/* ------------------------------------------------------------------ *
 *  De keten: welke handtekening is nu aan de beurt
 *
 *  Casper: "Je moet zorgen dat je te valideren hebt (administratie) daarna
 *  eerste handtekening (die kan ik dan kiezen in instellingen) Tweede
 *  handtekening (dit kan ik weer kiezen) Afronding (deze is altijd johannes,
 *  maar zal ik zelf neerzetten)."
 *
 *      open  ->  eerste_akkoord  ->  tweede_akkoord  ->  goedgekeurd
 *       te         eerste            tweede             afronding
 *     valideren  handtekening      handtekening       (en naar Exact)
 *
 *  Waarom dit een eigen bestand is
 *  -------------------------------
 *
 *  Omdat deze vraag op vier plekken wordt gesteld -- de knop in de lijst, de
 *  knop in het detail, de statuskolom en het goedkeuren zelf -- en vier
 *  antwoorden die uit de pas lopen is precies hoe een factuur bij de
 *  verkeerde persoon komt te liggen. Hier staat hij één keer, als pure
 *  functie, zodat de zelftest hem kan narekenen.
 *
 *  De database is de baas
 *  ----------------------
 *
 *  Alles hier staat ook in expenses_vier_ogen() (0173). Dat is met opzet
 *  dubbel, en de volgorde is niet omkeerbaar: de database weigert wat niet
 *  mag, ook bij een wijziging die uit de wachtrij komt en nooit een scherm
 *  heeft gezien. Dit bestand is de beleefdheid -- het laat de goede knop zien
 *  en geeft een nette melding in plaats van een databasefout.
 * ------------------------------------------------------------------ */

import type { Expense, ExpenseStatus } from './types'

/** Welke van de drie handtekeningen dit is. */
export type Handtekening = 'valideren' | 'eerste' | 'tweede' | 'afronding'

/** Of een stap geldt. Komt uit de instellingen; de database vraagt hetzelfde. */
export interface Ketenregels {
  /** Vier ogen (0060): moet er een aparte eerste beoordeling zijn? */
  vierOgen: boolean
  /** Afronding (0173): is er na de tweede handtekening nog een stap? */
  afronding: boolean
}

export interface Ketenstap {
  /** Welke handtekening nu aan de beurt is. */
  stap: Handtekening
  /** De stand waar de bon heen gaat als er getekend wordt. */
  naar: ExpenseStatus
  /** Wie deze stap mag zetten. Leeg = iedereen die over kosten beslist. */
  groep: string[]
  groepNaam: string[]
  /** Wie er al tekenden. Daar mag je niet nog eens bij zitten. */
  alGetekend: string[]
  alGetekendNaam: string[]
}

/** Wat er op het scherm staat als knop en als kop. */
export const STAPNAAM: Record<Handtekening, string> = {
  valideren: 'Valideren',
  eerste: 'Eerste handtekening',
  tweede: 'Tweede handtekening',
  afronding: 'Afronding',
}

/**
 * Dezelfde vier stappen, maar als OPDRACHT op een knop.
 *
 * Casper: "Alleen ipv akkoord moet je dus, valideer, zet 1ste handtekening,
 * zet 2de handtekening en Afronden ervan maken, zodat degene die ermee bezig
 * is, weet welke straat hij staat."
 *
 * STAPNAAM hierboven benoemt de stap ("Tweede handtekening") en staat in
 * kolommen en kopjes. Op een knop hoort een werkwoord: je leest wat er
 * gebeurt als je erop drukt, niet hoe de stap heet. En "Akkoord" is op geen
 * van de vier het goede woord -- bij valideren teken je niets, en bij de
 * afronding gaat hij de deur uit naar de boekhouding.
 */
export const KNOPNAAM: Record<Handtekening, string> = {
  valideren: 'Valideren',
  eerste: 'Zet 1ste handtekening',
  tweede: 'Zet 2de handtekening',
  afronding: 'Afronden',
}

/** Wat deze persoon met deze bon kan, op dit moment. */
export interface Beslissing {
  /** Welke handtekening aan de beurt is, of null als er niets meer te doen is. */
  stap: Ketenstap | null
  /** Valt er nu iets te beslissen? */
  teBeslissen: boolean
  /** Wat er op de hoofdknop hoort te staan. */
  knop: string
  /** Zette deze persoon de vorige handtekening zelf? */
  doorMij: boolean
  /** Tekent hij buiten de naamlijst om (0193: dan hoort er een reden bij)? */
  buitenRoute: boolean
  /** Mag hij ondanks doorMij toch tekenen, met een reden erbij (0151)? */
  magZelfBeide: boolean
}

/**
 * Wat mag ik met DEZE factuur?
 *
 * Dit stond als vijf losse stukjes in Kostenposten.tsx -- stapVan, isDubbel,
 * isBuitenRoute, magZelfBeide en teBeslissen -- in een bestand van bijna
 * zesduizend regels, en `magZelfBeide` was er letterlijk
 * `user.roles.includes('management')` middenin het scherm.
 *
 * Ze staan hier omdat er een tweede beeld bij komt. Een telefoon vraagt
 * precies hetzelfde als een bureaublad, en zou iemand die vijf regels daar
 * opnieuw opschrijven, dan ligt dezelfde factuur bij twee verschillende
 * mensen afhankelijk van welk scherm je opent. Bij een handtekening is dat
 * geen schoonheidsfoutje.
 *
 * De database blijft hoe dan ook de baas: expenses_vier_ogen() weigert een
 * tweede handtekening van dezelfde persoon ook als dit bestand iets anders
 * zou beweren. Wat hier staat is wat het SCHERM mag aanbieden, zodat iemand
 * niet op een knop drukt die toch wordt geweigerd.
 */
export function beslissingVoor(
  bon: Expense | undefined,
  wie: string,
  rollen: readonly string[],
  instellingen: Record<string, string>,
): Beslissing {
  const stap = bon ? volgendeStap(bon, regelsVoor(instellingen, bon.amountExcl)) : null
  return {
    stap,
    teBeslissen: stap !== null,
    knop: stap ? KNOPNAAM[stap.stap] : 'Goedkeuren',
    doorMij: tekendeZelfAl(stap, wie),
    buitenRoute: !!bon && buitenDeRoute(stap, wie),
    /* Alleen management mag over de vier ogen heen, en dan met een reden.
       Dat stond in het scherm; de database kent dezelfde regel (0151). */
    magZelfBeide: rollen.includes('management'),
  }
}

const schoon = (lijst: (string | undefined)[] | undefined): string[] =>
  (lijst ?? []).map((x) => (x ?? '').trim()).filter(Boolean)

/**
 * Welke stappen er voor dit bedrag gelden.
 *
 * Neemt de instellingen als gewone map aan in plaats van ze zelf op te halen.
 * Dat is er met opzet: het scherm heeft ze toch al (ze synchroniseren mee) en
 * moet dit voor tweehonderd regels tegelijk kunnen uitrekenen zonder
 * tweehonderd keer te wachten. En een pure functie is te controleren.
 *
 * Dezelfde vraag staat in de database als vier_ogen_nodig() en
 * afronding_nodig(). Die zijn de baas; dit is wat het scherm gebruikt om de
 * goede knop te tonen.
 */
export function regelsVoor(
  instellingen: Record<string, string>, bedrag: number,
): Ketenregels {
  const aan = (sleutel: string) =>
    (instellingen[sleutel] ?? 'ja').trim().toLowerCase() === 'ja'

  const vanaf = (sleutel: string) => {
    const n = Number((instellingen[sleutel] ?? '0').replace(',', '.'))
    return Number.isFinite(n) ? n : 0
  }

  /*
   * Math.abs(), en dat is geen schoonheidsfoutje maar een verschil met de
   * database dat de wachtrij laat vastlopen.
   *
   * Migratie 0194 zette abs() in vier_ogen_nodig() en afronding_nodig():
   *
   *     and abs(coalesce(bedrag_in, 0)) >= coalesce(...)
   *
   * met als reden "vijfduizend terug is net zo goed vijfduizend". Hier bleef
   * `bedrag >= drempel` staan. Bij een creditnota van -2.064 wijzen de twee
   * daarmee een ANDERE kant op:
   *
   *     client  -2064 >= 0   onwaar  -> geen vier ogen, geen afronding,
   *                                     dus volgendeStap() zegt: afronden
   *     server  abs() = 2064 >= 0  waar -> eerst de tweede handtekening
   *
   * Het scherm toont dan de knop Afronden, de wijziging gaat de wachtrij in,
   * en de server weigert hem -- acht keer, en daarna blijft hij staan. Zo
   * stond er op 16 oktober 2026 een creditnota van Bovemij in de wachtrij
   * van Johannes met "Deze factuur moet eerst de tweede handtekening
   * krijgen", terwijl zijn scherm hem als afgerond liet zien.
   *
   * De kop hierboven zegt het al: de database is de baas. Dan hoort dit
   * dezelfde som te zijn, en niet bijna dezelfde. Zie scripts/selftest/
   * wachtrij.ts groep 134, die de twee tegen elkaar nameet.
   */
  const geldt = (schakelaar: string, drempel: string) =>
    aan(schakelaar) && Math.abs(bedrag ?? 0) >= vanaf(drempel)

  return {
    vierOgen: geldt('vier_ogen', 'vier_ogen_vanaf'),
    afronding: geldt('afronding', 'afronding_vanaf'),
  }
}

/**
 * Welke stap er nu aan de beurt is, of niets als er niets meer te tekenen is.
 *
 * Twee dingen die hier met opzet gebeuren:
 *
 * 1. Staat vier ogen uit, dan is de EERSTE stap meteen de tweede
 *    handtekening. Dat klinkt vreemd en is het niet: "vier ogen uit" betekent
 *    dat er één handtekening nodig is, en die handtekening is dezelfde als de
 *    tweede was -- met dezelfde groep en dezelfde plek in de historie. Zou
 *    hij de 'eerste' heten, dan lag hij ineens bij een andere groep zodra
 *    iemand die schakelaar omzet.
 *
 * 2. Staat de afronding uit, dan gaat de tweede handtekening rechtstreeks
 *    naar 'goedgekeurd' -- precies zoals het vóór 0173 ging.
 */
export function volgendeStap(bon: Expense, regels: Ketenregels): Ketenstap | null {
  if (bon.status === 'goedgekeurd' || bon.status === 'afgekeurd') return null

  /*
   * Verkoop kent geen handtekeningen (0192).
   *
   * Casper, over het verkoopstuk: "Hier is het enkel valideren." In Blue10 is
   * de historie van een verkoopfactuur Gevalideerd -> Geboekt -> Gearchiveerd,
   * en dat is ook wat het ís: een factuur die wij zelf hebben gestuurd hoeft
   * niet door twee mensen te worden goedgekeurd. Hij hoeft nagekeken te
   * worden -- staat het goede bedrag erop, de goede klant, de goede periode.
   *
   * Daarna is hij klaar om te boeken. exact_verkoop_wachtend() pakt een
   * verkoopfactuur op zodra hij gevalideerd is; 'goedgekeurd' hoeft er voor
   * verkoop niet meer tussen te zitten.
   */
  if (bon.richting === 'verkoop') {
    if (bon.status !== 'open') return null
    return {
      stap: 'valideren',
      naar: 'gevalideerd',
      groep: [],
      groepNaam: [],
      alGetekend: [],
      alGetekendNaam: [],
    }
  }

  const eersteDoor = schoon([bon.eersteDoor])
  const eersteNaam = schoon([bon.eersteDoorNaam])
  const tweedeDoor = schoon([bon.tweedeDoor])
  const tweedeNaam = schoon([bon.tweedeDoorNaam])

  if (bon.status === 'tweede_akkoord') {
    return {
      stap: 'afronding',
      naar: 'goedgekeurd',
      groep: schoon(bon.afronders),
      groepNaam: schoon(bon.afrondersNaam),
      alGetekend: [...eersteDoor, ...tweedeDoor],
      alGetekendNaam: [...eersteNaam, ...tweedeNaam],
    }
  }

  /*
   * Eerst valideren (0190).
   *
   * Geen groep: iedereen in de administratie mag nakijken of het stuk klopt.
   * Dat is geen beslissing over geld maar over gegevens, en daar hoort geen
   * naamlijst bij -- wie het ziet, kan het nakijken.
   */
  if (bon.status === 'open') {
    return {
      stap: 'valideren',
      naar: 'gevalideerd',
      groep: [],
      groepNaam: [],
      alGetekend: [],
      alGetekendNaam: [],
    }
  }

  if (bon.status === 'gevalideerd' && regels.vierOgen) {
    return {
      stap: 'eerste',
      naar: 'eerste_akkoord',
      groep: schoon(bon.eersteBij),
      groepNaam: schoon(bon.eersteBijNaam),
      alGetekend: [],
      alGetekendNaam: [],
    }
  }

  /* 'eerste_akkoord', of 'gevalideerd' terwijl vier ogen uitstaat. */
  return {
    stap: 'tweede',
    naar: regels.afronding ? 'tweede_akkoord' : 'goedgekeurd',
    groep: schoon(bon.goedkeurders),
    groepNaam: schoon(bon.goedkeurdersNaam),
    alGetekend: eersteDoor,
    alGetekendNaam: eersteNaam,
  }
}

/**
 * Wacht deze factuur nog op een mens?
 *
 * Dat is de vraag achter elke teller op dit scherm: de badge in het menu, het
 * getal bij "Te valideren", en of er een knoppenrij komt. Die stonden alle
 * drie als een eigen lijstje standen in de .tsx, en bij 0173 bleek wat daar
 * mis mee is -- er kwam een stand bij en elk van die lijstjes moest apart
 * worden bijgewerkt. Een vergeten lijstje telt stil te weinig.
 */
export function wachtOpIemand(bon: Expense): boolean {
  /*
   * Voor verkoop is gevalideerd de eindstand (0192), niet goedgekeurd. Zonder
   * deze regel blijft elke nagekeken verkoopfactuur voorgoed in de tellers
   * staan als "wacht op iemand", terwijl er niemand meer aan de beurt is --
   * en dan is de badge in het menu een getal dat nooit meer op nul komt.
   */
  if (bon.richting === 'verkoop') return bon.status === 'open'

  return bon.status !== 'goedgekeurd' && bon.status !== 'afgekeurd'
}

/**
 * Zou deze persoon vastlopen op "je tekende zelf al"?
 *
 * Niet of het mag -- dat beslist de database, en management mag het met een
 * reden erbij (0151). Dit is de vraag die het scherm stelt om te weten of er
 * om een bevestiging gevraagd moet worden.
 */
export function tekendeZelfAl(stap: Ketenstap | null, wie: string): boolean {
  return !!stap && stap.alGetekend.includes(wie)
}

/**
 * Teken je hiermee buiten de route om? (0193)
 *
 * Casper: "Je moet zorgen dat managment het wel gewoon kan blijven overwriden
 * he" -- en toen ik vroeg of die klep smaller moest: "verplichte reden".
 *
 * Dit is de vraag die het scherm stelt om te weten of het om die reden moet
 * vragen. De database stelt hem ook, en die heeft het laatste woord: daar
 * hangt de klep aan is_management(), hier weten we alleen of er namen staan
 * waar jij niet bij hoort.
 *
 * Een LEGE groep telt met opzet niet als "buiten de route". Dat is geen
 * noodklep maar een gat in de inrichting, en daar komt ook management niet
 * langs -- vragen om een reden zou dan een belofte zijn die de database niet
 * waarmaakt.
 */
export function buitenDeRoute(stap: Ketenstap | null, wie: string): boolean {
  if (!stap) return false
  if (stap.groep.length === 0) return false
  return !stap.groep.includes(wie)
}

/**
 * Is dit een afronding die niet van jou is? (0226)
 *
 * Casper: "Zorg trouwens ook dat enkel johannes het 'afronden' kan doen."
 * Voor deze ene stap is er geen noodklep meer: wie niet bij de afronders
 * staat, krijgt de knop niet -- ook management niet, ook niet met een reden.
 * De database zet de afronders vast op afronden_door() en weigert de rest;
 * dit is wat het scherm daarvan laat zien.
 */
export function afrondenNietVanMij(stap: Ketenstap | null, wie: string): boolean {
  return !!stap && stap.stap === 'afronding' && stap.groep.length > 0 && !stap.groep.includes(wie)
}

/**
 * Ligt deze bon nu bij MIJ?
 *
 * Gebruikt om na het tekenen door te springen naar de volgende bon die op
 * jou wacht. Casper: "Door laten gaan naar de volgende, zorg ervoor dat als
 * dit er geen meer zijn, je wel in het menu terug komt."
 *
 * Drie dingen, en in deze volgorde:
 *
 *   geen stap      de bon is af of afgekeurd; dan wacht hij op niemand
 *   al getekend    jij zette deze handtekening al, dus jij bent het niet
 *   de groep       leeg betekent iedereen die over kosten beslist, en dus
 *                  ook jij; staat er wel een groep, dan moet jij erin zitten
 *
 * Let op wat dit NIET is: een rechtencontrole. Of je mag tekenen beslist de
 * database (expenses_vier_ogen), en het management mag met een reden erbij
 * ook een tweede handtekening van zichzelf zetten. Dit is de vraag "hoort
 * deze in mijn stapel" -- en wie zelf al tekende hoort daar niet in, ook al
 * zou hij het met een bevestiging alsnog kunnen. Doorspringen naar een bon
 * die meteen om een waarschuwing vraagt is geen doorstromen.
 */
export function mijnBeurt(stap: Ketenstap | null, wie: string): boolean {
  if (!stap) return false
  if (tekendeZelfAl(stap, wie)) return false
  /*
   * Alleen op NAAM (20 okt 2026). Casper, over TW10 Online: "Wat ik nog mis,
   * is dat je echt bij je eigen werk terecht komt. nu kom je ergens terecht
   * waar jijzelf niet in de route zit." Een lege groep telde hier als
   * "iedereen", en daarmee lag elke te valideren factuur (valideren heeft
   * geen naam, 0190) en elke stap zonder route bij iedereen. Die staan nu in
   * hun eigen bak (bakkenVan in lib/werklijst.ts), zoals op de telefoon al.
   */
  return stap.groep.includes(wie)
}

/**
 * Wat er in de statuskolom komt: bij wie het nu ligt.
 *
 * Casper, over de lijst met facturen: "Zorg er ook voor dat de namen van de
 * mensen erbij staan bij een tweede handtekening die deze moeten doen."
 *
 * Hier stonden de namen van wie er AL tekende. Dat is wat er gebeurd is; de
 * vraag op dit scherm is wat er moet gebeuren. Met alleen de eerdere namen
 * weet je wie je NIET hoeft te vragen -- en dan ga je alsnog zoeken.
 *
 * Een lege groep betekent niet "niemand" maar "iedereen die over kosten
 * beslist". Dat hoort er voluit te staan, anders leest een factuur zonder
 * groep als stuurloos terwijl hij juist door iedereen opgepakt kan worden.
 */
export function ligtBij(stap: Ketenstap): { tekst: string; titel: string } {
  const namen = stap.groepNaam
  const kort = STAPNAAM[stap.stap].toLowerCase()

  const tekst = namen.length
    ? `wacht op ${namen.join(', ')}`
    : `wacht op ${stap.stap === 'afronding' ? 'afronding' : kort.split(' ')[0]}`

  const titel = [
    stap.alGetekendNaam.length
      ? `Al getekend door ${stap.alGetekendNaam.join(' en ')}.`
      : null,
    namen.length
      ? `${STAPNAAM[stap.stap]} door ${namen.join(', ')}.`
      : `De ${kort} mag door iedereen die over kosten beslist.`,
    stap.stap === 'afronding'
      ? 'Daarna gaat de factuur naar Exact.'
      : null,
  ].filter(Boolean).join(' ')

  return { tekst, titel }
}

/* ------------------------------------------------------------------ *
 *  Wie er twee keer tekende, en waarom dat mocht
 *
 *  Casper, melding M-2610-0007: "Dezelfde persoon zette allebei de
 *  handtekeningen: gaat goed -- Kan toch niet? Staat rood bovenin links in
 *  me header voor de facturen te keuren."
 *
 *  Twee dingen klopten daar niet, en het tweede is het ergste.
 *
 *  1. De zin las als een OORDEEL. Er stond
 *
 *         {naam} zette allebei de handtekeningen: {zelfBeideWaarom}
 *
 *     en zelfBeideWaarom is vrije tekst die de ondertekenaar zelf intypt.
 *     Stond daar "gaat goed", dan las het alsof het systeem zei dat het goed
 *     ging. Het was een citaat, en het zag er niet uit als een citaat.
 *
 *  2. Er stond de VERKEERDE NAAM. De banner toonde approvedByName, en dat is
 *     wie afrondde -- niet wie twee keer tekende. Gemeten op 16 oktober 2026:
 *     vijf facturen waarbij Milos Boekhorst de eerste en de tweede zette en
 *     Johannes afrondde. Er stond dus de naam van Johannes bij iets dat
 *     Milos had gedaan.
 *
 *  Daarom staat het hier, als pure functie, en niet als een rij vraagtekens
 *  in een scherm: de vraag "wie tekende er twee keer" is te beantwoorden uit
 *  de drie namen die op de bon staan, en dat is na te rekenen.
 * ------------------------------------------------------------------ */

export interface DubbelGetekend {
  /** Wie er meer dan één keer tekende, of null als dat niet af te leiden is. */
  naam: string | null
  /** Welke twee stappen dezelfde persoon deed, in gewone woorden. */
  welke: string
  /** Wat er als reden is opgegeven. Altijd een citaat, nooit een oordeel. */
  reden: string
}

export function dubbelGetekend(bon: {
  eersteDoor?: string; eersteDoorNaam?: string
  tweedeDoor?: string; tweedeDoorNaam?: string
  approvedBy?: string; approvedByName?: string
  zelfBeideWaarom?: string
}): DubbelGetekend | null {
  const reden = (bon.zelfBeideWaarom ?? '').trim()
  if (reden === '') return null

  /*
   * Op het ID vergelijken en niet op de naam. Twee mensen kunnen dezelfde
   * naam hebben, en een naam die bij het aanmaken anders is gespeld zou
   * twee keer tekenen onzichtbaar maken.
   */
  const een = bon.eersteDoor
  const twee = bon.tweedeDoor
  const af = bon.approvedBy

  if (een && twee && een === twee) {
    return {
      naam: bon.eersteDoorNaam ?? bon.tweedeDoorNaam ?? null,
      welke: 'de eerste én de tweede handtekening',
      reden,
    }
  }
  if (af && twee && af === twee) {
    return {
      naam: bon.approvedByName ?? bon.tweedeDoorNaam ?? null,
      welke: 'de tweede handtekening én de afronding',
      reden,
    }
  }
  if (af && een && af === een) {
    return {
      naam: bon.approvedByName ?? bon.eersteDoorNaam ?? null,
      welke: 'de eerste handtekening én de afronding',
      reden,
    }
  }

  /*
   * De reden staat er wel, maar de namen wijzen het niet aan.
   *
   * Dat kan bij oude bonnen: tot 0173 was de tweede handtekening de laatste
   * stap en stond hij in approvedBy, dus tweedeDoor is dan leeg. Dan is er
   * geen naam om te noemen -- en een naam RADEN is precies de fout die deze
   * functie moet oplossen. Liever geen naam dan de verkeerde.
   */
  return { naam: null, welke: 'meer dan één handtekening', reden }
}
